<script setup lang="ts">
import { useSpace } from "@/api/space-query";
import {
	useArchiveUser,
	useDeleteUserAvatar,
	useResetUserPassword,
	userResendActivationLink,
	userResetPasswordUserRequest,
	useUpdateUser,
	useUpdateUserAvatar,
	useUpdateUserEmail,
	useUpdateUserRole,
} from "@/api/user-query";
import AppAvatar from "@/components/common/AppAvatar.vue";
import SettingsFooter from "@/components/common/SettingsFooter.vue";
import FileSelectHelper from "@/components/helpers/FileSelectHelper.vue";
import { wheneverOnce } from "@/composable/whenever-once";
import ConfigSection from "@/layouts/content/ConfigSection.vue";
import { useSnackbarStore } from "@/stores";
import { useQueryManager } from "@/stores/query-manager";
import { useConfirmLeave } from "@/use/confirm-leave";
import { isEqualForKeys } from "@/utils/compare";
import {
	mdiAccountCircle,
	mdiAccountCircleOutline,
	mdiCheckCircleOutline,
	mdiCloseCircleOutline,
	mdiDelete,
	mdiEmailOutline,
	mdiFormTextboxPassword,
} from "@mdi/js";
import { useForm } from "@soft-db/form";
import { Role, UpdateUser, UserWithDetails } from "@soft-db/monitoring-api-client";
import {
	Badge,
	Button,
	confirm,
	Divider,
	FormField,
	Icon,
	InputGroup,
	TextInput,
	Tooltip,
} from "@soft-db/ui";
import * as _ from "lodash-es";
import DesktopOnly from "../common/DesktopOnly.vue";
import PasswordField from "../common/PasswordField.vue";
import FeatureGuard from "../permission/FeatureGuard.vue";
import PermissionGuard from "../permission/PermissionGuard.vue";
import RoleField from "./RoleField.vue";
import UserFeatureToggle from "./UserFeatureToggle.vue";

type Form = UpdateUser & {
	email: string;
	role: Role;
	password: string;
};

const props = defineProps<{
	id: string;
	user: UserWithDetails;
	rolesAllowed: Role[];
}>();
const emit = defineEmits<{ back: [] }>();

const { t } = useI18n();

const id = computed(() => props.id);
const user = computed(() => props.user);

const isGenericUser = computed(() => user.value?.auth.type === "username");

const hasEmail = computed(() => user.value.auth.type === "email");

function goBack() {
	emit("back");
}

const { useField, setValues, values, handleSubmit, clear } = useForm<Form>();

const name = useField("name", { isRequired: true, min: 4 });
const { data: dashboard } = useField("features.dashboard");
const { data: integrity } = useField("features.integrity");

const email = useField("email", { isRequired: hasEmail.value, isEmail: true });

const newPassword = useField("password", {
	isRequired: false,
	min: 8,
});

const role = useField("role", { isRequired: true });

function syncUser(user: UserWithDetails) {
	setValues({
		name: user.name,
		features: user.features,
		email: user.auth.type === "email" ? user.auth.email : undefined,
		role: user.role,
	});
}

wheneverOnce(user, (user) => syncUser(user), { immediate: true });

const spaceId = computed(() => (user.value.scope.type === "space" ? user.value.scope.id : ""));
const { space } = useSpace(spaceId);

const showFeatures = computed(() => {
	switch (role.data.value) {
		case "sys_admin":
		case "admin":
			return false;
		default:
			return true;
	}
});

const showDashboardWarning = computed(() => space.value && !space.value.features.dashboard);
const showIntegrityWarning = computed(() => space.value && !space.value.features.integrity);

const hasUserChanged = computed(() => {
	return !isEqualForKeys(values.value, user.value, ["name", "features"]);
});

const hasEmailChanged = computed(() => {
	if (user.value == null || user.value.auth.type !== "email") return false;

	return email.data.value !== user.value.auth.email;
});

const hasPasswordChanged = computed(() => {
	return !!newPassword.data.value;
});

const hasRoleChanged = computed(() => {
	if (user.value == null) return false;
	return role.data.value !== user.value.role;
});

const hasChanges = computed(() => {
	if (user.value == null) return false;

	return (
		hasUserChanged.value ||
		hasEmailChanged.value ||
		hasPasswordChanged.value ||
		hasRoleChanged.value
	);
});

function undoChanges() {
	clear();
	if (user.value) {
		syncUser(user.value);
	}
}

const updateUser = useUpdateUser();
const updateEmail = useUpdateUserEmail();
const updateRole = useUpdateUserRole();
const resetPassword = useResetUserPassword();
const queryManager = useQueryManager();

const snackbar = useSnackbarStore();

const save = handleSubmit(async (values) => {
	if (!user.value) return;

	try {
		await queryManager.chainInvalidation(async () => {
			let updatedUser: UserWithDetails | undefined;

			if (hasUserChanged.value) {
				updatedUser = await updateUser.mutateAsync({
					id: id.value,
					data: {
						name: values.name,
						preferredLang: values.preferredLang,
						features: values.features,
					},
				});
			}

			if (hasEmailChanged.value && user.value.auth.type === "email") {
				updatedUser = await updateEmail.mutateAsync({
					id: id.value,
					email: values.email,
				});
				// Send Email Verification if the previous email was not verified.
				if (!user.value.auth.verified) await resendVerificationLink();
			}

			if (hasRoleChanged.value) {
				updatedUser = await updateRole.mutateAsync({
					id: id.value,
					role: values.role,
				});
			}

			if (hasPasswordChanged.value && values.password) {
				await resetPassword.mutateAsync({
					id: id.value,
					password: values.password,
				});
				newPassword.clear();
			}

			if (updatedUser) syncUser(updatedUser);
		});
		snackbar.openOnSaved();
	} catch {
		snackbar.openOnError();
	}
});

useConfirmLeave(() => hasChanges.value, t("common.unsavedChanges"));

const resetPasswordRequest = userResetPasswordUserRequest();
async function sendResetPasswordRequest() {
	if (user.value.auth.type !== "email") return;

	try {
		await resetPasswordRequest.mutateAsync({ email: user.value.auth.email });
		snackbar.openOnMessage(t("users.emailSent"));
	} catch {
		snackbar.openOnError();
	}
}

const activationRequest = userResendActivationLink();
async function resendVerificationLink() {
	try {
		await activationRequest.mutateAsync({ id: user.value.id });
		snackbar.openOnMessage(t("users.emailSent"));
	} catch {
		snackbar.openOnError();
	}
}

const userArchive = useArchiveUser();
async function archiveUser() {
	if (!(await confirm.yesNo(t("user.confirmArchive")))) {
		return;
	}

	try {
		await userArchive.mutateAsync(id.value);
		snackbar.openOnSaved();
		goBack();
	} catch {
		snackbar.openOnError();
	}
}

const updateAvatarMutation = useUpdateUserAvatar();
async function updateAvatar(file: File) {
	await updateAvatarMutation.mutateAsync({ id: id.value, image: file });
	snackbar.openOnSaved();
}

const deleteAvatarMut = useDeleteUserAvatar();
async function deleteAvatar() {
	await deleteAvatarMut.mutateAsync({ id: id.value });
	snackbar.openOnSaved();
}
</script>

<template>
	<div>
		<ConfigSection :title="t('object.user', 1)" :description="t('profile.userSettings')">
			<div class="flex items-center gap-4">
				<FileSelectHelper
					v-if="isGenericUser"
					class="flex size-48 items-center justify-center rounded-full"
					:showDelete="!!user?.avatarUrl"
					@update="updateAvatar"
					@delete="deleteAvatar"
				>
					<AppAvatar class="size-full" outlined :src="user?.avatarUrl" :icon="mdiAccountCircle" />
				</FileSelectHelper>

				<AppAvatar
					v-else
					class="size-48"
					outlined
					:src="user?.avatarUrl"
					:icon="mdiAccountCircle"
				/>

				<div v-if="user.auth.type === 'username'">
					<h5>{{ t("common.username") }}</h5>
					<Badge>{{ user.auth.username }}</Badge>
				</div>
			</div>
			<FormField class="w-full" :label="t('profile.fullName')" :errors="name.errors.value">
				<InputGroup class="w-full">
					<Icon :d="mdiAccountCircleOutline" />
					<TextInput v-model="name.data.value" />
				</InputGroup>
			</FormField>
			<template v-if="user.auth.type === 'email'">
				<FormField class="w-full" :label="t('common.email')" :errors="email.errors.value">
					<InputGroup class="w-full">
						<Icon :d="mdiEmailOutline" />
						<TextInput v-model="email.data.value" />
						<Tooltip
							:tooltip="user.auth.verified ? t('users.emailVerified') : t('users.emailNotVerified')"
						>
							<Icon
								:d="user.auth.verified ? mdiCheckCircleOutline : mdiCloseCircleOutline"
								:class="user.auth.verified ? 'text-success' : ''"
							/>
						</Tooltip>
					</InputGroup>
				</FormField>
				<div v-if="user.auth.verified" class="flex justify-between pl-1">
					<span>{{ t("users.sendResetPassword") }}</span>
					<Button class="w-fit" color="primary" @click="sendResetPasswordRequest">
						{{ t("users.resetPassword") }}
					</Button>
				</div>
				<div v-else class="flex justify-between pl-1">
					<span>{{ t("users.emailNotVerified") }}</span>
					<Button class="w-fit" color="primary" @click="resendVerificationLink">
						{{ t("users.resendVerificationLink") }}
					</Button>
				</div>
			</template>
			<template v-else>
				<input
					:value="user.auth.type === 'username' ? user.auth.username : null"
					autocomplete="username"
					hidden
				/>
				<PasswordField
					v-model="newPassword.data.value"
					class="w-full"
					autocomplete="new-password"
					:icon="mdiFormTextboxPassword"
					:label="t('common.password')"
					:placeholder="t('users.changePassword')"
					:errors="newPassword.errors.value"
				/>
			</template>
		</ConfigSection>
		<Divider />

		<ConfigSection :title="t('space.access')" :description="t('users.accessDescription')">
			<RoleField
				v-model="role.data.value"
				:disabled="isGenericUser"
				:disabledReason="t('users.genericUserRoleDisabled')"
				:scope="user.scope.type"
				:availableRoles="rolesAllowed"
			/>

			<template v-if="showFeatures">
				<span class="text-xs">{{ t("space.features") }}</span>
				<div>
					<FeatureGuard feature="dashboard">
						<UserFeatureToggle
							v-model="dashboard"
							feature="dashboard"
							:showWarning="showDashboardWarning && dashboard"
						/>
					</FeatureGuard>

					<FeatureGuard feature="integrity">
						<UserFeatureToggle
							v-model="integrity"
							feature="integrity"
							:showWarning="showIntegrityWarning && integrity"
						/>
					</FeatureGuard>
				</div>
			</template>
		</ConfigSection>

		<SettingsFooter class="mt-6" :hasChanges="hasChanges" @undo="undoChanges" @save="save">
			<template #prepend>
				<DesktopOnly>
					<PermissionGuard permission="user.delete">
						<Button v-if="!hasChanges" variant="ghost" size="xs" color="error" @click="archiveUser">
							<Icon :d="mdiDelete" />
							{{ t("users.archive") }}
						</Button>
					</PermissionGuard>
				</DesktopOnly>
			</template>
		</SettingsFooter>
	</div>
</template>
